import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import type { SwipeDirection } from "pageSwipe";
import * as utils from "shared/utils";
import type ReaderTextStore from "stores/ReaderTextStore";
import type ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import { EndOfText } from "views/EndOfTextView";
import ReaderPopoverLayer, {
  schedulePopoverReposition,
} from "views/readerText/ReaderPopoverLayer";
import ReaderTextContent from "views/readerText/ReaderTextContent";

type Props = {
  bookID?: string;
  endOfText?: boolean;
  noFooter?: boolean;
  onMarkCompleted?: () => void;
  onStartReviewSession?: (toPosition: number) => void;
  onSwipe?: (direction: SwipeDirection) => void;
  readerTextStore?: ReaderTextStore;
  readerViewStore?: ReaderViewStore;
  /**
   * Footer rendered after the text in scroll mode (YouTube transcripts):
   * a "finished watching" button which swaps to the end-of-text panel once
   * the text is marked completed.
   */
  scrollFooter?: "completedButton" | "endOfText";
  scrollMode?: boolean;
  textDirection?: "rtl";
};

export default class ReaderTextIsland extends preact.Component<Props> {
  private disposeEffect?: () => void;
  /** How the shown page came, for it to slide in (in the app, as iOS's pages) */
  private pageTurn?: "next" | "previous";

  componentDidMount() {
    this.bindTextVisibleEffect();
  }

  componentDidUpdate(previousProps: Props) {
    // Re-subscribe when the page swaps in a fresh view store (e.g. the
    // edit-document refresh re-initializes stores without remounting the
    // preserved reader page).
    if (previousProps.readerViewStore !== this.props.readerViewStore) {
      this.bindTextVisibleEffect();
    }
  }

  componentWillUnmount() {
    this.disposeEffect?.();
  }

  private bindTextVisibleEffect() {
    this.disposeEffect?.();
    this.disposeEffect = effect(() => {
      const readerViewStore = this.props.readerViewStore;
      // Showing a page: how it came (a turn, which it can't know while hidden
      // as "to" pages lay out once more from where they turn out to start)
      if (readerViewStore?.textVisible.value) {
        this.pageTurn = deviceDetect.isIOSApp()
          ? readerViewStore.pageTurn.peek()
          : undefined;
        readerViewStore.setPageTurn(undefined);
      }
      this.forceUpdate();
    });
  }

  render() {
    const { readerTextStore, readerViewStore } = this.props;
    const textVisible = readerViewStore?.textVisible.value ?? true;

    return (
      <>
        <div
          id="pageTextViewport"
          class={utils.cn({
            noFooter: this.props.noFooter,
            endOfTextPage: this.props.endOfText,
          })}
          style={this.props.scrollMode ? { overflowY: "scroll" } : undefined}
        >
          <div
            id="pageText"
            dir={this.props.textDirection}
            class={utils.cn({
              textVisible,
              turnedToNext: this.pageTurn === "next",
              turnedToPrevious: this.pageTurn === "previous",
            })}
            onClick={this.onClick}
            // Popovers measured mid-slide are put back by their words
            onAnimationEnd={schedulePopoverReposition}
          >
            {this.props.endOfText ? (
              <EndOfText
                model={stores.bookModel}
                remoteUserWords={stores.remoteUserWords}
              />
            ) : (
              <>
                {readerTextStore && (
                  // Keyed by store identity: the reader page is preserved
                  // across page turns within a book but swaps in a fresh
                  // ReaderTextStore per page load, and the content tree
                  // subscribes to the store's signals on mount.
                  <ReaderTextContent
                    key={readerTextStore.instanceKey}
                    store={readerTextStore}
                    scrollMode={this.props.scrollMode}
                    onSwipe={this.props.onSwipe}
                  />
                )}
                {this.props.scrollFooter === "completedButton" && (
                  <div class="scrollViewFooter">
                    <button class="shareButton" onClick={this.onMarkCompleted}>
                      <i class="icon-trophy"></i> I've Finished Watching
                    </button>
                  </div>
                )}
                {this.props.scrollFooter === "endOfText" && (
                  <div class="scrollViewFooter">
                    <EndOfText
                      model={stores.bookModel}
                      remoteUserWords={stores.remoteUserWords}
                      starterStory={stores.bookModel.book.value.starterStory}
                      scrollMode
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {readerTextStore && !this.props.endOfText && (
          <ReaderPopoverLayer
            key={readerTextStore.instanceKey}
            store={readerTextStore}
            readerViewStore={readerViewStore}
          />
        )}
      </>
    );
  }

  // Section-break review buttons are rendered from the token tree as plain
  // anchors, so their clicks are delegated from the container.
  private onClick = (event: MouseEvent) => {
    const reviewButton = (event.target as Element | null)?.closest?.(
      ".startReviewSession"
    );
    if (!reviewButton) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.props.onStartReviewSession?.(
      parseInt(reviewButton.getAttribute("data-to") ?? "", 10)
    );
  };

  private onMarkCompleted = () => {
    this.props.onMarkCompleted?.();
  };
}
